import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react'

import {
  CollectionServerSideFeatureFlags,
  FEATURE_FLAG_TYPE,
  FILTER_TYPE,
  FilterType,
  UrlPlacement,
} from '@src/server-side/server-feature-flags/feature-flag.types'

import { ECollectionPageType } from './services/collectionService/CollectionService.types'

export interface CollectionFeatureFlags {
  featureFlagType: typeof FEATURE_FLAG_TYPE.COLLECTION
  subCollectionUrlPlacement: UrlPlacement
  collectionPageType: ECollectionPageType
  filterType: FilterType

  isPersonalized: boolean
  bestSellerExperimentBucket: string | null
  velouFiltersExperimentBucket: string | null
  clpPersonalisationExperimentBucket: string | null
  isBestSellerPersonalizedExp: boolean
  recurringItemSubscriptionsExperimentBucket: string | null
}

const CONTEXT_ERROR_MESSAGE = 'useCollectionFeature must be used within a CollectionFeatureProvider'

export const CollectionFeatureContext = createContext<CollectionFeatureFlags | null>(null)

export const useCollectionFeature = (): CollectionFeatureFlags => {
  const context = useContext(CollectionFeatureContext)

  if (!context) {
    throw new Error(CONTEXT_ERROR_MESSAGE)
  }

  return context
}

/** Non-throwing variant — returns null when rendered outside CollectionFeatureProvider. */
export const useCollectionFeatureOptional = (): CollectionFeatureFlags | null => useContext(CollectionFeatureContext)

interface CollectionFeatureProviderProps {
  serverSideFeatureFlags: CollectionServerSideFeatureFlags | null | undefined
}

export const CollectionFeatureProvider: FC<PropsWithChildren<CollectionFeatureProviderProps>> = ({
  children,
  serverSideFeatureFlags,
}) => {
  const featureFlags = useMemo(
    (): CollectionFeatureFlags => ({
      featureFlagType: FEATURE_FLAG_TYPE.COLLECTION,
      // Mirrors the dynamic-config default (enabled=false → query); path mode requires explicit opt-in.
      subCollectionUrlPlacement: serverSideFeatureFlags?.subCollectionUrlPlacement ?? 'query',
      collectionPageType: serverSideFeatureFlags?.collectionPageType ?? ECollectionPageType.ALL,
      filterType: serverSideFeatureFlags?.filterType ?? FILTER_TYPE.DROPDOWN,
      isPersonalized: serverSideFeatureFlags?.isPersonalized ?? false,
      bestSellerExperimentBucket: serverSideFeatureFlags?.bestSellerExperimentBucket ?? null,
      velouFiltersExperimentBucket: serverSideFeatureFlags?.velouFiltersExperimentBucket ?? null,
      clpPersonalisationExperimentBucket: serverSideFeatureFlags?.clpPersonalisationExperimentBucket ?? null,
      isBestSellerPersonalizedExp: serverSideFeatureFlags?.isBestSellerPersonalizedExp ?? false,
      recurringItemSubscriptionsExperimentBucket:
        serverSideFeatureFlags?.recurringItemSubscriptionsExperimentBucket ?? null,
    }),
    [serverSideFeatureFlags],
  )

  return <CollectionFeatureContext.Provider value={featureFlags}>{children}</CollectionFeatureContext.Provider>
}
